import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, CheckCircle, XCircle, Lock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_dcdf0d5b-e3c7-46d7-864d-f9a117f62290/artifacts/q3fbs21n_Logo.png';

const SetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const token = searchParams.get('token');

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setValidating(false);
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/auth/validate-invite-token?token=${token}`);
        if (response.data.valid) {
          setTokenValid(true);
          setUserInfo(response.data);
        }
      } catch (error) {
        console.error('Token validation failed:', error);
        setTokenValid(false);
      } finally {
        setValidating(false);
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`${API_URL}/auth/set-password`, {
        token,
        new_password: password,
      });

      setSuccess(true);
      toast.success('Password set successfully!');
      
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set password');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || validating) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-zinc-100">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-zinc-400" />
            <p className="mt-4 text-zinc-500">Validating your invite...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-zinc-100">
          <CardContent className="p-8 text-center">
            <XCircle className="h-12 w-12 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-semibold text-zinc-900 mb-2">Invalid or Expired Link</h2>
            <p className="text-zinc-500 mb-6">
              This invite link is invalid or has expired. Please contact your administrator to request a new invite.
            </p>
            <Button onClick={() => navigate('/login')} variant="outline">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-zinc-100">
          <CardContent className="p-8 text-center">
            <CheckCircle className="h-12 w-12 mx-auto text-emerald-500 mb-4" />
            <h2 className="text-xl font-semibold text-zinc-900 mb-2">Password Set!</h2>
            <p className="text-zinc-500 mb-6">
              Your password has been set successfully. Redirecting you to login...
            </p>
            <Loader2 className="h-5 w-5 animate-spin mx-auto text-zinc-400" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-zinc-100">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <img src={LOGO_URL} alt="The T Touch" className="h-12 w-auto mix-blend-multiply" />
          </div>
          <CardTitle className="text-2xl">Set Your Password</CardTitle>
          <CardDescription>
            Welcome, {userInfo?.full_name}! Create a secure password for your account.
          </CardDescription>
        </CardHeader>

        <CardContent className="pt-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={userInfo?.email || ''}
                disabled
                className="mt-2 bg-zinc-50"
              />
            </div>

            <div>
              <Label htmlFor="password">New Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                className="mt-2"
                required
                minLength={8}
                data-testid="new-password-input"
              />
            </div>

            <div>
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your password"
                className="mt-2"
                required
                data-testid="confirm-password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={submitting || !password || !confirmPassword}
              data-testid="set-password-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Setting Password...
                </>
              ) : (
                <>
                  <Lock className="mr-2 h-4 w-4" />
                  Set Password & Continue
                </>
              )}
            </Button>
          </form>

          <p className="text-xs text-zinc-400 text-center mt-6">
            By setting your password, you agree to the T Touch terms of service.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default SetPasswordPage;
